import { Box, Paper, Typography } from "@mui/material";
import summaryIcons from "assets/icons/icons";
import { useRTL } from "hooks/isRTL";

import { useQuery } from "@tanstack/react-query";
import { GetDashboardCounts } from "services/dashboard";
import {
  cardConfig,
  IssueTypeEnum,
  orderStatusCard,
  RegistrationStatusEnum,
  OrderTypeEnum,
} from "enums/shared";
import LoadingBanner from "Components/shared/LoadingBanner";
import CustomButton from "Components/shared/Button";
import DropdownMenu from "Components/shared/DropdownMenu";
import { useNavigate } from "react-router-dom";
import { REFETCH_INTERVAL_TIME } from "enums/shared";
import { useState } from "react";

/**
 * Displays order statistics in a card layout with icons, titles, and values.
 *
 * Fetches data from the dashboard counts API and renders cards dynamically based on the `cardConfig` enums.
 *
 * @component
 * @returns {JSX.Element} The dashboard order statistics UI.
 */
function DashboardOrderStatistics({
  onCardClick,
  selectedTypes,
  onClearFilters,
  selectedOrderType,
  setSelectedOrderType,
}) {
  const { t } = useRTL();
  const navigate = useNavigate();
  // `selectedOrderType` is lifted to parent (DashboardPage) so other dashboard components
  // (e.g. attention/issue list) can consume the same filter.

  const {
    data: dashboardCounts,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
  } = useQuery({
    queryKey: ["dashboardCounts", selectedOrderType],
    queryFn: () => GetDashboardCounts(selectedOrderType),
    refetchInterval: REFETCH_INTERVAL_TIME,
  });

  //enums
  const cardData = cardConfig.map((card) => ({
    ...card,
    value: dashboardCounts?.[card.title] ?? "na",
  }));

  const newIssueCards = Object.entries(IssueTypeEnum).map(
    ([key, { value, color, iconKey }]) => ({
      type: Number(key),
      title: value,
      count: dashboardCounts?.[value] ?? 0,
      color,
      iconKey,
    })
  );

  const handleCardNavigation = (cardTitle) => {
    if (cardTitle === "activeDrivers") {
      navigate(`/driverManagement`, {
        state: { status: [RegistrationStatusEnum.active] },
      });
      return;
    }

    let status = null;
    if (cardTitle === "completedOrders") {
      status = [orderStatusCard.delivered];
    } else if (cardTitle === "incompleteOrders") {
      status = [orderStatusCard.incompleteData];
    } else if (cardTitle === "rescheduledOrders") {
      status = [orderStatusCard.rescheduled];
    } else if (cardTitle === "unAssignedOrders") {
      status = [orderStatusCard.awaitingSelection];
    } else if (cardTitle === "activeOrders") {
      status = [
        orderStatusCard.pendingPickup,
        orderStatusCard.pickUpStarted,
        orderStatusCard.arrivedAtPickup,
        orderStatusCard.orderCollected,
        orderStatusCard.orderOutForDelivery,
        orderStatusCard.arrivedAtDropOff,
        orderStatusCard.onHold,
        orderStatusCard.rescheduled,
      ];
    }

    if (status) {
      navigate(`/orders`, { state: { status } });
    }
  };

  if (isDashboardLoading) {
    return <LoadingBanner isLoading={true} message="Loading..." />;
  }

  if (isDashboardError) {
    return <Typography>{t("errorLoadingData")}</Typography>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        width: "62%",
        marginBottom: "0.5rem",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: "1rem",
            alignItems: "center",
          }}
        >
          <Typography variant="h5">{t("ordersStatistics")}</Typography>
          <DropdownMenu
            placeholder={t("orderType")}
            options={[
              { value: OrderTypeEnum.regular, label: t("regular") },
              { value: OrderTypeEnum.expedited, label: t("expedited") },
            ]}
            value={selectedOrderType}
            onChange={(value) => setSelectedOrderType(value)}
          />
        </Box>
        <CustomButton onClick={onClearFilters}>
          {t("clearFilters")}
        </CustomButton>
      </Box>
      {/* First row: first two cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {/* the index in cardData is based on the the requerment where the first tow cards have different style and not clickable  */}
        {cardData.slice(0, 2).map((card, index) => (
          <Paper
            key={index}
            elevation={2}
            sx={{
              backgroundColor: "background.white",
              borderRadius: "14px",
              padding: "1.5rem",
              flex: `1 1 calc(${card.span * 25}% - 1rem)`,
              maxWidth: `calc(${card.span * 25}% - 1rem)`,
              display: "flex",
              flexDirection: "column",
              cursor: index === 1 ? "pointer" : "default", // only second one is clickable
              "&:hover": index === 1 ? { backgroundColor: "#d1cfcf" } : {},
            }}
            onClick={
              index === 1 ? () => handleCardNavigation(card.title) : undefined
            }
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0",
              }}
            >
              <Box
                component="img"
                src={summaryIcons[card.iconKey]}
                alt={card.title}
                sx={{ width: "40px", height: "40px" }}
              />

              <Typography
                variant="h4"
                sx={{
                  color: card.color,
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  paddingLeft: "0.9rem",
                }}
              >
                {card.value}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 600,
                    lineHeight: "24px",
                    color: "text.cardsGray",
                  }}
                >
                  {t(card.title)}
                </Typography>
              </Typography>
            </Box>
          </Paper>
        ))}
      </Box>

      {/* Second row: remaining cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap", // force cards to stay in one row
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {cardData.slice(2).map((card, index) => (
          <Paper
            key={index + 2}
            elevation={2}
            sx={{
              backgroundColor: "background.white",
              borderRadius: "14px",
              paddingY: "1.5rem",
              paddingX: "1rem",
              flex: "1 1 calc(100%/5 - 1rem)",
              maxWidth: "calc(100%/5 - 1rem)",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer", // only the second row cards are clickable
              "&:hover": { backgroundColor: "#d1cfcf" }, //ask d1cfcf or f4f4f4
            }}
            onClick={() => handleCardNavigation(card.title)}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.5rem",
              }}
            >
              <Box
                component="img"
                src={summaryIcons[card.iconKey]}
                alt={card.title}
                sx={{ width: "25px", height: "25px" }}
              />

              <Typography
                variant="h6"
                sx={{
                  color: card.color,
                }}
              >
                {card.value}
              </Typography>
            </Box>

            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.9rem",
                color: "text.black",
              }}
            >
              {t(card.title)}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* new cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          gap: "1rem",
        }}
      >
        {newIssueCards.map((card, index) => (
          <Paper
            key={`issue-${index}`}
            elevation={2}
            sx={{
              backgroundColor: "background.white",
              borderRadius: "14px",
              padding: "1.5rem",
              flex: "1 1 calc(25% - 1rem)",
              maxWidth: "calc(25% - 1rem)",
              display: "flex",
              flexDirection: "column",
              cursor: "pointer",
              border: selectedTypes.includes(card.type)
                ? `4px solid ${card.color}7a`
                : "none",
              "&:hover": {
                backgroundColor: "#d1cfcf",
              },
            }}
            onClick={() => onCardClick(card.type)} //passing
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                marginBottom: "0.5rem",
              }}
            >
              <Box
                component="img"
                src={summaryIcons[card.iconKey]}
                alt={card.title}
                sx={{ width: "40px", height: "40px" }}
              />
              <Typography
                variant="h4"
                sx={{
                  color: card.color,
                }}
              >
                {card.count}
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: "24px",
                color: "text.black",
              }}
            >
              {t(card.title)}
            </Typography>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

export default DashboardOrderStatistics;
